import { Attr, BelongsTo, HasMany, HasOne, Model } from "spraypaint";

import { getLocaleText } from "shared/boot/i18n";
import { ApplicationRecord } from "shared/services/spraypaint";

import type ActiveStorageAttachment from "./ActiveStorageAttachment";
import type Organisation from "./Organisation";
import type OutreachActivity from "./OutreachActivity";
import type OutreachCommunicationContact from "./OutreachCommunicationContact";
import type OutreachCommunicationUser from "./OutreachCommunicationUser";
import type Tag from "./Tag";
import type User from "./User";

export enum OutreachCommunicationStatusType {
  cancelled = "cancelled",
  complete = "complete",
  declined = "declined",
  open = "open",
}

export interface OutreachCommunicationStatus {
  backgroundColor: string;
  color: string;
  label: string;
  value: OutreachCommunicationStatusType;
}

export enum OutreachCommunicationType {
  call = "call",
  email = "email",
  event = "event",
  external_media_release = "external_media_release",
  interview = "interview",
  meeting = "meeting",
  other = "other",
}

export const getOutreachCommunicationStatuses = () =>
  <OutreachCommunicationStatus[]>[
    {
      backgroundColor: "#D6E4FF",
      color: "#096DD7",
      label: getLocaleText("resources.outreach_communication.statuses.open"),
      value: OutreachCommunicationStatusType.open,
    },
    {
      backgroundColor: "#C5F7D0",
      color: "#4BA05D",
      label: getLocaleText(
        "resources.outreach_communication.statuses.complete"
      ),
      value: OutreachCommunicationStatusType.complete,
    },
    {
      backgroundColor: "#FFCCC7",
      color: "#CD1323",
      label: getLocaleText(
        "resources.outreach_communication.statuses.declined"
      ),
      value: OutreachCommunicationStatusType.declined,
    },
    {
      backgroundColor: "#FFCCC7",
      color: "#CD1323",
      label: getLocaleText(
        "resources.outreach_communication.statuses.cancelled"
      ),
      value: OutreachCommunicationStatusType.cancelled,
    },
  ];

export const getOutreachCommunicationTypes = () => [
  {
    color: "#FFAE6B",
    label: getLocaleText("resources.outreach_communication.types.call"),
    value: OutreachCommunicationType.call,
  },
  {
    color: "#7DED95",
    label: getLocaleText("resources.outreach_communication.types.email"),
    value: OutreachCommunicationType.email,
  },
  {
    color: "#597EF7",
    label: getLocaleText("resources.outreach_communication.types.meeting"),
    value: OutreachCommunicationType.meeting,
  },
  {
    color: "#B37FEB",
    label: getLocaleText("resources.outreach_communication.types.interview"),
    value: OutreachCommunicationType.interview,
  },
  {
    color: "#13C3C3",
    label: getLocaleText(
      "resources.outreach_communication.types.external_media_release"
    ),
    value: OutreachCommunicationType.external_media_release,
  },
  {
    color: "#6BC1FF",
    label: getLocaleText("resources.outreach_communication.types.event"),
    value: OutreachCommunicationType.event,
  },
  {
    color: "#FF85C0",
    label: getLocaleText("resources.outreach_communication.types.other"),
    value: OutreachCommunicationType.other,
  },
];

@Model()
class OutreachCommunication extends ApplicationRecord {
  static jsonapiType = "outreach_communications";

  @HasMany() activeStorageAttachments: ActiveStorageAttachment[];

  @Attr({ persist: false }) archivedAt: string;

  @Attr() communicationType: OutreachCommunicationType;

  @Attr({ persist: false }) createdAt: string;

  @Attr() notes: string;

  @BelongsTo() organisation: Organisation;

  @Attr() organisationId: number;

  @Attr() organisationTeamId: number;

  @HasOne({ name: "activity" }) outreachActivity: OutreachActivity;

  @HasMany()
  outreachCommunicationContacts: OutreachCommunicationContact[];

  @Attr({ persist: false }) outreachCommunicationContactsCount: number;

  @HasMany()
  outreachCommunicationUsers: OutreachCommunicationUser[];

  @Attr() private: boolean;

  @Attr() sentAt: string;

  @Attr() status: OutreachCommunicationStatusType;

  @HasMany({ name: "taggable" }) tags: Tag[];

  @Attr() title: string;

  @Attr({ persist: false }) updatedAt: string;

  @BelongsTo() user: User;

  @Attr() userId: number;
}

export default OutreachCommunication;
